import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/buying-or-selling-a-business-guelph',
  title: 'Buying or Selling a Business with Real Estate — Guelph, ON',
  description:
    "Business-with-real-estate transactions in Guelph, Ontario — where the property and the operation are sold together. Direct line to Pinnacle Commercial, no listings portal.",
};

const guelph = site.serviceAreas.find((a) => a.slug === 'guelph')!;

const FAQS: Faq[] = [
  {
    q: 'What makes a business-with-real-estate deal different from a regular commercial sale?',
    a: "You're valuing two things at once — the operating business and the property it sits on — and they don't always move together. Getting that split right, and structuring a deal that reflects it, is the core of this kind of transaction.",
  },
  {
    q: 'Do you have a list of businesses for sale in Guelph right now?',
    a: 'No public listings page — deliberately. Business sales need discretion; employees, customers and competitors usually shouldn\'t find out from a website. A direct, confidential conversation is how this actually works.',
  },
  {
    q: 'Can you help if I only want the real estate, not the business itself?',
    a: "Yes — some owners are selling the operation and keeping the building, or vice versa. Tell me which situation you're in and we'll structure the conversation around that.",
  },
  {
    q: 'How is confidentiality handled during the process?',
    a: "Carefully, and discussed upfront before any details go further than the two of us. Discretion isn't an afterthought in a business sale — it's part of how the deal has to be run.",
  },
];

export default function BusinessGuelph() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-business-guelph', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-04"
      kicker="Local search — business & real estate"
      h1="Buying or selling a business, Guelph."
      intro="Representation for Guelph transactions where the business and the real estate it operates from are sold together — a different kind of deal from a straight property sale, handled as one."
      glyph="office"
      audiences={[
        {
          label: 'Looking to buy',
          body: "Tell me the kind of operation and location you're after, and whether owning the real estate matters to you or not, and I'll help you find what's realistically available in Guelph.",
          ctaLabel: 'Tell me what you’re after',
        },
        {
          label: 'Looking to sell',
          body: 'A confidential first conversation about your business, your property, and how the two should be valued and positioned together — before anything goes to market.',
          ctaLabel: 'Start confidentially',
        },
      ]}
      localLabel="Guelph, business & real estate"
      localBody={<p>{guelph.blurb}</p>}
      honestLabel="No listings page, by design"
      honestBody="Business sales run on confidentiality, not public exposure — so there's no listings page here, and there isn't going to be one. What you get instead is a direct, discreet conversation about your specific situation."
      faqs={FAQS}
      interest="business-listing"
      closingLine="Buying or selling a business in Guelph — start with a confidential conversation."
      related={[
        { to: '/business-listings', label: 'Full business listings services' },
        { to: '/sell-my-commercial-property-kitchener-waterloo', label: 'Sell commercial property — KW region' },
        { to: '/about', label: 'About Pinnacle Commercial' },
      ]}
    />
  );
}
